<template>
    <div>
        <app-bar :title="t('premium_user_feedbacks_title')"></app-bar>

        <template v-if="touchedFeedback.exists()">
            <v-app-bar
                color="transparent"
                height="84"
                elevation="0"
                class="mb-n6 px-6"
            >
                <v-toolbar-title>#{{ touchedFeedback.id }}</v-toolbar-title>
                <v-btn
                    @click="unset()"
                    plain
                    icon
                    absolute
                    right
                    class="mr-n8"
                ><v-icon>mdi-close</v-icon></v-btn>
            </v-app-bar>

            <div class="px-10 pb-6 overflow-hidden">
                <v-flex>
                    <div class="float-left mr-10">
                        <card-unit :items="[
                            { text: t('premium_label_user_id'), value: touchedFeedback.userId, route: { name: 'trainees.show', params: { id: touchedFeedback.userId } } },
                        ]">
                            <template #title>{{ t('feedback_card_title_user') }}</template>
                        </card-unit>

                        <card-unit :items="[
                            { text: touchedFeedback.comment || '-' },
                        ]">
                            <template #title>{{ t('feedback_card_title_comment') }}</template>
                        </card-unit>
                    </div>
                    <div class="float-left">
                        <card-unit :items="[
                            { text: t('premium_user_feedback_label_id'), value: touchedFeedback.id },
                            { text: t('premium_user_feedback_label_feedback_date'), value: touchedFeedback.createdAt ? formatDate(touchedFeedback.createdAt) : '-' },
                            { text: t('premium_label_program_name'), value: touchedFeedback.trainingProgramName || '-', route: touchedFeedback.trainingProgramId ? { name: 'programs.show', params: { id: touchedFeedback.trainingProgramId } } : undefined },
                        ]">
                            <template #title>{{ t('feedback_card_title_general') }}</template>
                        </card-unit>
                    </div>
                </v-flex>
            </div>
        </template>

        <v-layout>
            <template v-if="! touchedFeedback.exists()">
                <v-progress-linear
                    v-if="overlay"
                    absolute
                    indeterminate
                ></v-progress-linear>

                <card-collection
                    :meta="meta"
                    :busy="overlay"
                    @updated="updated"
                >
                    <template #filters>
                        <v-toolbar
                            elevation="0"
                            color="transparent"
                            class="mt-7"
                            height="56"
                        >
                            <v-text-field
                                v-model="queries.search"
                                @click:clear="clearSearch"
                                class="input-search md mr-2"
                                clearable
                                outlined
                                dense
                                hide-details
                                prepend-inner-icon="mdi-magnify"
                                :label="t('general_label_search')"
                            ></v-text-field>

                            <v-btn
                                @click="search"
                                class="sm mx-5"
                                color="primary"
                            >{{ t('general_action_search') }}</v-btn>

                            <v-spacer></v-spacer>

                            <v-btn
                                v-ripple="false"
                                plain
                                color="orange darken-4"
                                @click="reset"
                            >{{ t('general_action_reset_filters') }}</v-btn>
                        </v-toolbar>
                    </template>

                    <template #data>
                        <thead>
                        <tr>
                            <th>{{ t('premium_label_user_id') }}</th>
                            <th>{{ t('premium_user_feedback_label_id') }}</th>
                            <th>{{ t('premium_user_feedback_label_feedback') }}</th>
                            <th>{{ t('premium_user_feedback_label_feedback_date') }}</th>
                            <th>{{ t('premium_label_program_name') }}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="collection.data.length === 0">
                            <td class="text-center" colspan="5">{{ t('general_text_no_data') }}</td>
                        </tr>
                        <tr v-for="item in collection.data" :key="item.id">
                            <td>
                                <router-link :to="{ name: 'trainees.show', params: { id: item.userId } }">{{ item.userId }}</router-link>
                            </td>
                            <td>
                                <a href="javascript:" @click.prevent="touch(item)">{{ item.id }}</a>
                            </td>
                            <td class="feedback-comment-cell">
                                <a href="javascript:" @click.prevent="touch(item)" :title="item.comment || ''">{{ item.comment || '-' }}</a>
                            </td>
                            <td>{{ item.createdAt ? formatDate(item.createdAt) : '-' }}</td>
                            <td>
                                <router-link
                                    v-if="item.trainingProgramId"
                                    :to="{ name: 'programs.show', params: { id: item.trainingProgramId } }"
                                    class="program-name-cell"
                                    :title="item.trainingProgramName || ''"
                                >#{{ item.trainingProgramId }} {{ item.trainingProgramName }}</router-link>
                                <span v-else>-</span>
                            </td>
                        </tr>
                        </tbody>
                    </template>

                    <template #export>
                        <v-btn
                            v-if="collection.data.length > 0"
                            @click="_export"
                            class="exs mt-4 ml-4"
                            color="primary"
                            small
                        >{{ t('general_action_export') }}</v-btn>
                    </template>
                </card-collection>
            </template>
        </v-layout>
    </div>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, onMounted, toRefs, ref } from '@vue/composition-api'
import { useI18n } from 'vue-i18n-composable'
import { useStore } from '@/store'
import { usePremiumHttp } from '@/core/api/premium'
import { PremiumUserFeedback } from '@/core/models/premium-user-feedback'
import CardCollection from '@/components/CardCollection.vue'
import CardUnit from '@/components/CardUnit.vue'

export default defineComponent({
    components: {
        CardCollection,
        CardUnit,
    },

    setup() {
        const state = reactive({
            overlay: true,
            meta: undefined,
        })

        const queries = reactive({
            search: <string | undefined>undefined,
            page: 1,
            per_page: 20,
        })

        const store = useStore()
        const collection = computed(() => store.getters['premiumUserFeedbackCollection/get'])
        const touchedFeedback = ref(new PremiumUserFeedback(<any>{}))

        const capture = () => {
            state.overlay = true
            store.dispatch('premiumUserFeedbackCollection/get', { ...queries }).then((data: any) => {
                state.meta = data.meta
                state.overlay = false
            })
        }

        const updated = (updatedQueries: any) => {
            queries.page = updatedQueries.page
            capture()
        }

        const touch = (item: PremiumUserFeedback) => {
            Object.assign(touchedFeedback.value, item)
        }

        const unset = () => {
            touchedFeedback.value.unset()
        }

        const _export = () => {
            state.overlay = true
            usePremiumHttp().exportUserFeedbacks({ ...queries }).then((data: any) => {
                state.overlay = false
                const e = document.createElement('a')
                e.href = window.URL.createObjectURL(data)
                e.setAttribute('download', 'User feedbacks.csv')
                document.body.appendChild(e)
                e.click()
                document.body.removeChild(e)
            })
        }

        const formatDate = (dateStr: string) => {
            const d = new Date(dateStr)
            return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        }

        const search = () => capture()

        const clearSearch = () => {
            queries.search = undefined
            capture()
        }

        const reset = () => {
            queries.search = undefined
            queries.page = 1
            capture()
        }

        onMounted(() => capture())

        return {
            queries,
            collection,
            touchedFeedback: <PremiumUserFeedback>touchedFeedback.value,
            touch,
            unset,
            updated,
            _export,
            formatDate,
            search,
            clearSearch,
            reset,
            ...toRefs(state),
            ...useI18n(),
        }
    },
})
</script>

<style scoped>
.feedback-comment-cell {
    max-width: 280px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.program-name-cell {
    display: block;
    max-width: 160px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
